import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  addYmdDays,
  buildChangeOneDayExceptions,
  changeOneDayHourOptions,
  ymdInTimeZone,
  zonedDateFromParts,
} from '../../util/changeOneDay';
import { formatHour12 } from '../../util/availabilitySummary';
import { talkerDayOfWeek } from '../../util/talkerAvailability';

import css from './ChangeOneDayEditor.module.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const pad = n => (n < 10 ? `0${n}` : `${n}`);
const ymdFromParts = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

const monthCells = (year, month) => {
  const firstDow = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= lastDate; day += 1) {
    cells.push(day);
  }
  return cells;
};

const formatYmd = (ymd, intl) => {
  if (!ymd) {
    return '';
  }
  const [year, month, day] = ymd.split('-').map(part => Number.parseInt(part, 10));
  return intl.formatDate(new Date(year, month - 1, day), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const usualLabel = (ymd, entries, timeZone) => {
  if (!ymd) {
    return null;
  }
  const day = talkerDayOfWeek(zonedDateFromParts(ymd, '12:00', timeZone), timeZone);
  const row = (entries || []).find(entry => entry?.dayOfWeek === day);
  if (!row) {
    return null;
  }
  return `${formatHour12(row.startTime)}–${formatHour12(row.endTime)}`;
};

const MonthGrid = ({ view, onShift, selected, rangeStart, rangeEnd, todayYmd, onPick, label }) => {
  const intl = useIntl();
  const cells = monthCells(view.year, view.month);
  const heading = intl.formatDate(new Date(view.year, view.month, 1), {
    month: 'long',
    year: 'numeric',
  });
  return (
    <div className={css.cal}>
      <div className={css.calNav}>
        <button
          type="button"
          className={css.calArrow}
          onClick={() => onShift(-1)}
          aria-label={intl.formatMessage({ id: 'ChangeOneDayEditor.prevMonth' })}
        >
          ‹
        </button>
        <p className={css.calLabel}>{heading}</p>
        <button
          type="button"
          className={css.calArrow}
          onClick={() => onShift(1)}
          aria-label={intl.formatMessage({ id: 'ChangeOneDayEditor.nextMonth' })}
        >
          ›
        </button>
      </div>
      <div className={css.calDow} aria-hidden="true">
        {WEEKDAYS.map(day => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className={css.calGrid} role="grid" aria-label={label}>
        {cells.map((day, index) => {
          if (!day) {
            return <span key={`e-${index}`} className={css.calEmpty} />;
          }
          const ymd = ymdFromParts(view.year, view.month, day);
          const past = ymd < todayYmd;
          const isStart = ymd === rangeStart;
          const isEnd = rangeEnd && ymd === rangeEnd;
          const inRange = rangeStart && rangeEnd && ymd > rangeStart && ymd < rangeEnd;
          return (
            <button
              key={ymd}
              type="button"
              disabled={past}
              className={classNames(css.calDay, {
                [css.calToday]: ymd === todayYmd,
                [css.calOn]: ymd === selected || isStart || isEnd,
                [css.calRange]: inRange,
              })}
              onClick={() => onPick(ymd)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Change one day / time off editor. Saves Sharetribe availability exceptions.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.timeZone]
 * @param {Array} [props.entries]
 * @param {Function} props.onSubmit
 * @param {Function} [props.onCancel]
 * @param {boolean} [props.inProgress]
 * @param {string} [props.error]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const ChangeOneDayEditor = props => {
  const {
    timeZone = 'Etc/UTC',
    entries = [],
    onSubmit,
    onCancel,
    inProgress = false,
    error = null,
    className,
  } = props;
  const intl = useIntl();
  const todayYmd = ymdInTimeZone(new Date(), timeZone);
  const startOptions = useMemo(() => changeOneDayHourOptions(false), []);
  const endOptions = useMemo(() => changeOneDayHourOptions(true), []);
  const today = new Date();
  const [startView, setStartView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [endView, setEndView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [startYmd, setStartYmd] = useState(null);
  const [endYmd, setEndYmd] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [mode, setMode] = useState('off');
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('17:00');

  const shiftView = (which, delta) => {
    const setter = which === 'end' ? setEndView : setStartView;
    setter(prev => {
      const date = new Date(prev.year, prev.month + delta, 1);
      return { year: date.getFullYear(), month: date.getMonth() };
    });
  };

  const usual = usualLabel(startYmd, entries, timeZone);
  const lastYmd = rangeOpen && endYmd && endYmd > startYmd ? endYmd : startYmd;
  const exceptions = buildChangeOneDayExceptions({
    startYmd,
    endYmd: lastYmd,
    mode,
    from,
    to,
    timeZone,
    entries,
  });

  const onSave = event => {
    event.preventDefault();
    if (!startYmd || !exceptions.length || typeof onSubmit !== 'function') {
      return;
    }
    onSubmit(exceptions);
  };

  const weekdayKey = startYmd
    ? talkerDayOfWeek(zonedDateFromParts(startYmd, '12:00', timeZone), timeZone)
    : null;
  const weekdayName = weekdayKey
    ? WEEKDAYS[WEEKDAY_KEYS.indexOf(weekdayKey)]
    : '';

  return (
    <form
      className={classNames(css.root, className)}
      onSubmit={onSave}
      data-testid="change-one-day-editor"
    >
      <div className={css.body} data-testid="change-one-day-body">
      <h2 className={css.title} id="take-a-day-off-title">
        <FormattedMessage id="ChangeOneDayEditor.title" />
      </h2>
      <p className={css.lede}>
        <FormattedMessage id="ChangeOneDayEditor.lede" />
      </p>

      <section className={css.step}>
        <h3 className={css.stepTitle}>
          <span className={css.stepNum}>1</span>
          <FormattedMessage id="ChangeOneDayEditor.pickDay" />
          <button
            type="button"
            className={css.rangeLink}
            onClick={() => {
              setRangeOpen(open => !open);
              if (rangeOpen) {
                setEndYmd(null);
              }
            }}
          >
            <FormattedMessage id="ChangeOneDayEditor.severalDays" />
          </button>
        </h3>
        <MonthGrid
          view={startView}
          onShift={delta => shiftView('start', delta)}
          selected={startYmd}
          rangeStart={startYmd}
          rangeEnd={lastYmd}
          todayYmd={todayYmd}
          onPick={ymd => {
            setStartYmd(ymd);
            if (endYmd && endYmd < ymd) {
              setEndYmd(null);
            }
          }}
          label={intl.formatMessage({ id: 'ChangeOneDayEditor.pickDay' })}
        />
        <p className={css.legend}>
          <FormattedMessage id="ChangeOneDayEditor.legend" />
        </p>
        {rangeOpen ? (
          <div className={css.rangePanel}>
            <p className={css.rangeHint}>
              <FormattedMessage id="ChangeOneDayEditor.rangeHint" />
            </p>
            <MonthGrid
              view={endView}
              onShift={delta => shiftView('end', delta)}
              selected={endYmd}
              rangeStart={startYmd}
              rangeEnd={endYmd}
              todayYmd={todayYmd}
              onPick={ymd => {
                if (startYmd && ymd < startYmd) {
                  setStartYmd(ymd);
                }
                setEndYmd(ymd);
              }}
              label={intl.formatMessage({ id: 'ChangeOneDayEditor.pickLastDay' })}
            />
          </div>
        ) : null}
      </section>

      <section className={css.step}>
        <h3 className={css.stepTitle}>
          <span className={css.stepNum}>2</span>
          <FormattedMessage id="ChangeOneDayEditor.whatChange" />
        </h3>
        <div className={css.choices} role="radiogroup">
          <label className={classNames(css.choice, { [css.choiceOn]: mode === 'off' })}>
            <input
              type="radio"
              name="change-one-day-mode"
              value="off"
              checked={mode === 'off'}
              onChange={() => setMode('off')}
            />
            <span className={css.choiceCard}>
              <span className={css.choiceTitle}>
                <FormattedMessage id="ChangeOneDayEditor.offTitle" />
              </span>
              <span className={css.choiceCopy}>
                <FormattedMessage id="ChangeOneDayEditor.offCopy" />
              </span>
            </span>
          </label>
          <label className={classNames(css.choice, { [css.choiceOn]: mode === 'hours' })}>
            <input
              type="radio"
              name="change-one-day-mode"
              value="hours"
              checked={mode === 'hours'}
              onChange={() => setMode('hours')}
            />
            <span className={css.choiceCard}>
              <span className={css.choiceTitle}>
                <FormattedMessage id="ChangeOneDayEditor.hoursTitle" />
              </span>
              <span className={css.choiceCopy}>
                <FormattedMessage id="ChangeOneDayEditor.hoursCopy" />
              </span>
            </span>
          </label>
        </div>
        {mode === 'hours' ? (
          <div className={css.hours}>
            {usual && weekdayName ? (
              <p className={css.hoursLabel}>
                <FormattedMessage
                  id="ChangeOneDayEditor.usualHours"
                  values={{ day: weekdayName, hours: usual }}
                />
              </p>
            ) : null}
            <div className={css.hoursRow}>
              <label className={css.timeLabel}>
                <FormattedMessage id="ChangeOneDayEditor.from" />
                <select className={css.select} value={from} onChange={e => setFrom(e.target.value)}>
                  {startOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={css.timeLabel}>
                <FormattedMessage id="ChangeOneDayEditor.to" />
                <select className={css.select} value={to} onChange={e => setTo(e.target.value)}>
                  {endOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ) : null}
      </section>

      {error ? (
        <p className={css.error} role="alert">
          {error}
        </p>
      ) : null}

      <p className={css.summary} aria-live="polite">
          {startYmd ? (
            <FormattedMessage
              id={mode === 'off' ? 'ChangeOneDayEditor.summaryOff' : 'ChangeOneDayEditor.summaryHours'}
              values={{
                when:
                  lastYmd && lastYmd !== startYmd
                    ? `${formatYmd(startYmd, intl)} – ${formatYmd(lastYmd, intl)}`
                    : formatYmd(startYmd, intl),
                hours: `${formatHour12(from)}–${formatHour12(to)}`,
                next: formatYmd(addYmdDays(lastYmd || startYmd, 1), intl),
              }}
            />
          ) : (
            <FormattedMessage id="ChangeOneDayEditor.summaryEmpty" />
          )}
        </p>
      </div>
      <div className={css.foot} data-testid="change-one-day-footer">
        <div className={css.actions}>
          {typeof onCancel === 'function' ? (
            <button type="button" className={css.cancel} onClick={onCancel}>
              <FormattedMessage id="ChangeOneDayEditor.cancel" />
            </button>
          ) : null}
          <button type="submit" className={css.save} disabled={!startYmd || inProgress}>
            <FormattedMessage
              id={inProgress ? 'ChangeOneDayEditor.saving' : 'ChangeOneDayEditor.save'}
            />
          </button>
        </div>
      </div>
    </form>
  );
};

export default ChangeOneDayEditor;
